/**
 * Simplified Permissions API module for Platform Access
 * 
 * 🚨 BREAKING CHANGE: Role system removed!
 * 
 * Roles are now just permissions with "role:" prefix:
 * - role:admin
 * - role:support-agent  
 * - role:editor
 * - role:viewer
 * 
 * Old: assign_role: { user, role: ["admin"] }
 * New: grant_permission: { user, permission: ["role:admin"] }
 */

import { api, generateCRUDInterface } from "aldoo-ra/API"

// Initialize CRUD API (roles removed - permissions only)
const crudAPI = api("crud")
const userCRUD = generateCRUDInterface(crudAPI, "User")
const permissionCRUD = generateCRUDInterface(crudAPI, "Permission")

// Permissions API endpoint
const permissionsAPI = api("permissions")

export const PermissionsPlatformAPI = {
  
  /**
   * Role Helper Methods (roles are now permissions with "role:" prefix)
   */
  
  /**
   * Assign role permissions to a user (using new role:rolename format)
   * @param {string} userId - User ObjectId
   * @param {Array<string>} roles - Array of role names (will be prefixed with "role:")
   * @returns {Promise<{result: boolean, error?: string}>}
   */
  AssignRoleToUser: async (userId, roles) => {
    try {
      // Convert role names to role: permissions
      const rolePermissions = roles.map(role => `role:${role}`)
      
      const response = await permissionsAPI({
        grant_permission: {
          user: userId,
          permission: rolePermissions
        }
      })
      
      return {
        result: response.result,
        error: response.error
      }
    } catch (error) {
      console.error("Failed to assign role to user:", error)
      return {
        result: false,
        error: error.message || "Failed to assign role to user"
      }
    }
  },

  /**
   * Remove role permissions from a user
   * @param {string} userId - User ObjectId
   * @param {Array<string>} roles - Array of role names (will be prefixed with "role:")
   * @returns {Promise<{result: boolean, error?: string}>}
   */
  RemoveRoleFromUser: async (userId, roles) => {
    try {
      // Convert role names to role: permissions
      const rolePermissions = roles.map(role => `role:${role}`)
      
      const response = await permissionsAPI({
        revoke_permission: {
          user: userId,
          permission: rolePermissions
        }
      })
      
      return {
        result: response.result,
        error: response.error
      }
    } catch (error) {
      console.error("Failed to remove role from user:", error)
      return {
        result: false,
        error: error.message || "Failed to remove role from user"
      }
    }
  },

  /**
   * Check if user has specific roles (checks for role: permissions)
   * @param {string} userId - User ObjectId
   * @param {Array<string>} roles - Array of role names (will be prefixed with "role:")
   * @returns {Promise<{result: boolean, error?: string, missing?: Array}>}
   */
  CheckUserRoles: async (userId, roles) => {
    try {
      // Convert role names to role: permissions
      const rolePermissions = roles.map(role => `role:${role}`)
      
      const response = await permissionsAPI({
        has_permission: {
          user: userId,
          permission: rolePermissions
        }
      })
      
      return {
        result: response.result,
        required: response.required,
        error: response.error
      }
    } catch (error) {
      console.error("Failed to check user roles:", error)
      return {
        result: false,
        error: error.message || "Failed to check user roles"
      }
    }
  },

  /**
   * Get available role permissions (role: prefixed permissions)
   * @returns {Promise<Array<string>>}
   */
  GetAvailableRoles: async () => {
    try {
      const response = await permissionsAPI({
        get_enabled_permissions: {}
      })
      
      if (response.permissions && Array.isArray(response.permissions)) {
        // Filter only role: permissions and remove the prefix
        const rolePermissions = response.permissions
          .filter(perm => perm.permission.startsWith('role:'))
          .map(perm => perm.permission.replace('role:', ''))
        return rolePermissions
      }
      return []
    } catch (error) {
      console.error("Failed to get available roles:", error)
      return []
    }
  },

  /**
   * Permission Management
   */
  
  /**
   * Get all permissions for a user (direct + inherited from roles)
   * @param {string} userId - User ObjectId
   * @returns {Promise<Array<string>>}
   */
  GetUserPermissions: async (userId) => {
    try {
      const response = await permissionsAPI({
        get_permission: {
          user: userId
        }
      })
      
      if (response.result && response.permissions) {
        return response.permissions
      }
      
      return []
    } catch (error) {
      console.error("Failed to get user permissions:", error)
      return []
    }
  },

  /**
   * Grant direct permissions to a user (not through roles)
   * @param {string} userId - User ObjectId
   * @param {Array<string>} permissions - Array of permission strings
   * @returns {Promise<{result: boolean, error?: string}>}
   */
  GrantPermissionToUser: async (userId, permissions) => {
    try {
      const response = await permissionsAPI({
        grant_permission: {
          user: userId,
          permissions: permissions
        }
      })
      
      return {
        result: response.result,
        error: response.error
      }
    } catch (error) {
      console.error("Failed to grant permission to user:", error)
      return {
        result: false,
        error: error.message || "Failed to grant permission to user"
      }
    }
  },

  /**
   * Revoke direct permissions from a user
   * @param {string} userId - User ObjectId
   * @param {Array<string>} permissions - Array of permission strings
   * @returns {Promise<{result: boolean, error?: string}>}
   */
  RevokePermissionFromUser: async (userId, permissions) => {
    try {
      const response = await permissionsAPI({
        revoke_permission: {
          user: userId,
          permissions: permissions
        }
      })
      
      return {
        result: response.result,
        error: response.error
      }
    } catch (error) {
      console.error("Failed to revoke permission from user:", error)
      return {
        result: false,
        error: error.message || "Failed to revoke permission from user"
      }
    }
  },

  /**
   * Check if a user has specific permissions
   * @param {string} userId - User ObjectId
   * @param {Array<string>} permissions - Array of permission strings
   * @returns {Promise<{result: boolean, error?: string, required?: Array}>}
   */
  CheckUserPermissions: async (userId, permissions) => {
    try {
      const response = await permissionsAPI({
        has_permission: {
          user: userId,
          permission: permissions
        }
      })
      
      return {
        result: response.result,
        required: response.required,
        error: response.error
      }
    } catch (error) {
      console.error("Failed to check user permissions:", error)
      return {
        result: false,
        error: error.message || "Failed to check user permissions"
      }
    }
  },

  /**
   * Clean up orphaned permissions using CRUD API
   * @returns {Promise<{result: boolean, deleted: number}>}
   */
  CleanupOrphanedPermissionsCRUD: async () => {
    try {
      const response = await crudAPI({
        delete: {
          collection: "permissions",
          match: { user: { $exists: false } }
        }
      })
      
      return response
    } catch (error) {
      console.error("Failed to cleanup orphaned permissions (CRUD):", error)
      return {
        result: false,
        deleted: 0,
        error: error.message || "Failed to cleanup orphaned permissions"
      }
    }
  },

  /**
   * Get users with their permissions (including role: permissions)
   * @param {Object} options - Query options
   * @returns {Promise<{result: boolean, data: Array, totalItems: number}>}
   */
  GetUsersWithPermissions: async (options = {}) => {
    try {
      let queryParams = {
        match: {
          deleted: { $ne: true },
          ...options.match
        },
        page: options.page || 1,
        itemsPerPage: options.itemsPerPage || 25,
        sort: options.sort || { created: -1 }
      }

      // Handle search functionality properly
      if (options.search && options.search.trim()) {
        const searchTerm = options.search.trim()
        queryParams.match = {
          ...queryParams.match,
          $or: [
            { name: { $regex: searchTerm, $options: "i" } },
            { email: { $regex: searchTerm, $options: "i" } }
          ]
        }
      }

      // Use CRUD API to get users
      const response = await userCRUD.Get(queryParams)
      
      return {
        result: response.result || true,
        data: response.data || [],
        totalItems: response.totalItems || 0
      }
    } catch (error) {
      console.error("Failed to get users with permissions:", error)
      return {
        result: false,
        data: [],
        totalItems: 0,
        error: error.message || "Failed to get users with permissions"
      }
    }
  },

  /**
   * Get available permissions in the system (now using resource definitions)
   * @returns {Promise<Array<string>>}
   */
  GetAvailablePermissions: async () => {
    try {
      const response = await permissionsAPI({
        get_enabled_permissions: {}
      })
      
      if (response.permissions && Array.isArray(response.permissions)) {
        const permissionNames = response.permissions.map(perm => perm.permission)
        return permissionNames
      }
      return []
    } catch (error) {
      console.error("Failed to get available permissions:", error)
      return []
    }
  },

  /**
   * Create a user permission record (initializes empty permissions for a user)
   * @param {string} userId - User ObjectId
   * @param {Array<string>} permissions - Initial permissions array (usually empty)
   * @returns {Promise<{result: boolean, error?: string}>}
   */
  CreateUserPermission: async (userId, permissions = []) => {
    try {
      // Use CRUD API to create a permission record for the user
      const response = await permissionCRUD.Create({
        user: userId,
        permissions: permissions,
        created: new Date().toISOString(),
        updated: new Date().toISOString()
      })

      return {
        result: response ? true : false,
        error: response ? null : "Failed to create permission record"
      }
    } catch (error) {
      console.error("Failed to create user permission record:", error)
      return {
        result: false,
        error: error.message || "Failed to create user permission record"
      }
    }
  },

  /**
   * Get all resource definitions
   * @param {Object} options - Query options
   * @returns {Promise<{result: boolean, resources: Array, error?: string}>}
   */
  GetResourceDefinitions: async (options = {}) => {
    try {
      const response = await permissionsAPI({
        get_resource_definitions: {
          category: options.category,
          resource: options.resource,
          enabledOnly: options.enabledOnly,
          isSystem: options.isSystem
        }
      })
      
      return {
        result: response.result !== false,
        resources: response.resources || response.data || [],
        error: response.error
      }
    } catch (error) {
      console.error("Failed to get resource definitions:", error)
      return {
        result: false,
        resources: [],
        error: error.message || "Failed to get resource definitions"
      }
    }
  }
}

export default PermissionsPlatformAPI